import { Result } from '@cplug/frontend-core/utils'
import { BaseGatewayErrorDTO, type DataWrappedDTO } from '@cplug/frontend-core/gateways'
import { isSuccessResponse, ErrorCodes } from '@cplug/frontend-core/models'
import type { DashboardUser } from '../models/DashboardUser'
import type { TwoFactorAdminStatus } from '../../user-two-factor-admin/models/TwoFactorAdminStatus'
import type { ResetUserTwoFactorByAdminRequest } from '../../user-two-factor-admin/models/ResetUserTwoFactorByAdminRequest'
import {
  TwoFactorAdminStatusDTO,
  mapDTOToTwoFactorAdminStatus
} from '../../user-two-factor-admin/gateways/DTOs/TwoFactorAdminStatusDTO'
import {
  mapResetUserTwoFactorByAdminRequestToDTO
} from '../../user-two-factor-admin/gateways/DTOs/ResetUserTwoFactorByAdminRequestDTO'
import { UserDTO, mapUserToDTO, mapDTOToUser } from './DTOs/UserDTO'
import { CPBaseCRUDGateway } from '~/shared/gateways/CPBaseCRUDGateway'

type ListDTO = { users: UserDTO[] }

export class CPUserGateway extends CPBaseCRUDGateway<UserDTO, ListDTO, DashboardUser> {
  protected apiUrlDomain = 'users'
  protected dtoToModelMapper = mapDTOToUser
  protected modelToDtoMapper = mapUserToDTO
  protected dtoSchema = UserDTO

  protected get listField (): 'users' {
    return 'users'
  }

  protected override get modelIdField (): keyof DashboardUser {
    return 'id'
  }

  async getLoggedUserInfo (): Promise<Result<DashboardUser>> {
    const response = await this.get<DataWrappedDTO<UserDTO> | BaseGatewayErrorDTO>('users/me')

    if (response === null || !isSuccessResponse(response)) {
      const errorDto = new BaseGatewayErrorDTO()
      return Result.ofError(errorDto.deserialize(response?.data as any) ?? { code: ErrorCodes.Unknown })
    }

    const responseData = response.data as DataWrappedDTO<UserDTO>
    if (!responseData?.data) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(mapDTOToUser(responseData.data))
  }

  async restore (userId: number): Promise<Result<void>> {
    const response = await this.post<DataWrappedDTO<UserDTO> | BaseGatewayErrorDTO>(`users/${userId}/restore`)

    if (response === null || !isSuccessResponse(response)) {
      const errorDto = new BaseGatewayErrorDTO()
      return Result.ofError(errorDto.deserialize(response?.data as any) ?? { code: ErrorCodes.Unknown })
    }

    return Result.ofValue(undefined)
  }

  async getTwoFactorAdminStatus (userId: number): Promise<Result<TwoFactorAdminStatus>> {
    const response = await this.get<DataWrappedDTO<TwoFactorAdminStatusDTO> | BaseGatewayErrorDTO>(
      `users/${userId}/two-factor/status`
    )

    if (response === null || !isSuccessResponse(response)) {
      const errorDto = new BaseGatewayErrorDTO()
      return Result.ofError(errorDto.deserialize(response?.data as any) ?? { code: ErrorCodes.Unknown })
    }

    const responseData = response.data as DataWrappedDTO<TwoFactorAdminStatusDTO>
    if (!responseData?.data) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    const parsed = TwoFactorAdminStatusDTO.safeParse(responseData.data)
    if (!parsed.success) {
      return Result.ofError({ code: ErrorCodes.Unknown })
    }

    return Result.ofValue(mapDTOToTwoFactorAdminStatus(parsed.data))
  }

  async resetUserTwoFactor (
    userId: number,
    data: ResetUserTwoFactorByAdminRequest
  ): Promise<Result<void>> {
    const response = await this.post<BaseGatewayErrorDTO>(
      `users/${userId}/two-factor/reset`,
      mapResetUserTwoFactorByAdminRequestToDTO(data)
    )

    if (response === null || !isSuccessResponse(response)) {
      const errorDto = new BaseGatewayErrorDTO()
      return Result.ofError(errorDto.deserialize(response?.data as any) ?? { code: ErrorCodes.Unknown })
    }

    return Result.ofValue(undefined)
  }

  async revokeAllSessionsForUser (userId: number): Promise<Result<void>> {
    const response = await this.post<BaseGatewayErrorDTO>(`users/${userId}/sessions/revoke-all`)

    if (response === null || !isSuccessResponse(response)) {
      const errorDto = new BaseGatewayErrorDTO()
      return Result.ofError(errorDto.deserialize(response?.data as any) ?? { code: ErrorCodes.Unknown })
    }

    return Result.ofValue(undefined)
  }
}
